import * as React from 'react'
import { useDispatch } from 'react-redux'
import ExternalIntegrationsApiService, { ExternalCityMappingModel, ExternalCityMappingPostModel } from '../../services/ExternalIntegrationsApiService'
import CitiesApiService from '../../services/CitiesApiService'
import { DataTable, DataTableColumnModel, DataTableModel, defaultLabelSort, FilterType, InputType, Select, SelectOptionModel } from 'traveldesk-ui'
import CountriesApiService from '../../services/CountriesApiService'
interface Props {
    apiProviderId: number | undefined
}

const AdminExternalCitiesMappings = (props: Props) => {
    const dispatch = useDispatch()
    const [citiesOptions, setCitiesOptions] = React.useState<Map<number, SelectOptionModel[]>>(new Map<number, SelectOptionModel[]>())
    const [externalCities, setExternalCities] = React.useState<ExternalCityMappingModel[]>([])
    const [countriesOptions, setCountriesOptions] = React.useState<SelectOptionModel[]>([])
    const citiesHash = React.useMemo(() => {
        return new Map<number, Map<number, string>>(
            Array.from(citiesOptions, ([countryId, options]) => [
                countryId,
                new Map<number, string>(options.map(option => [option.value as number, option.label])),
            ]),
        )
    }, [citiesOptions])
    React.useEffect(() => {
        if (externalCities?.length > 0) {
            const mappedCountries = Array.from(new Set(
                externalCities
                    .map(city => city.ourCountryId)
                    .filter((countryId): countryId is number => !!countryId),
            ))
            preloadCitiesOptions(mappedCountries)
        }
    }, [externalCities])
    const countriesHash = React.useMemo(() => {
        return countriesOptions.reduce((acc, curr) => {
            acc.set(curr.value as number, curr.label)
            return acc
        }, new Map<number, string>())
    }, [countriesOptions])
    React.useEffect(() => {
        loadCountriesOptions()
    }, [])
    React.useEffect(() => {
        if (props.apiProviderId) {
            getCities()
        }
    }, [props.apiProviderId])
    const preloadCitiesOptions = async (countryIds: number[]) => {
        if (countryIds.length === 0) {
            return
        }

        const response = await CitiesApiService.getCitiesInCountries(dispatch, countryIds)
        if (response.IsOk && response.Content) {
            const optionsByCountry = response.Content.reduce((result, city) => {
                const options = result.get(city.countryId) ?? []
                options.push({ label: city.name, value: city.id } as SelectOptionModel)
                result.set(city.countryId, options)
                return result
            }, new Map<number, SelectOptionModel[]>())

            optionsByCountry.forEach(options => options.sort(defaultLabelSort))
            setCitiesOptions(prevCitiesOptions => {
                const newCitiesOptions = new Map<number, SelectOptionModel[]>(prevCitiesOptions)
                optionsByCountry.forEach((options, countryId) => newCitiesOptions.set(countryId, options))
                return newCitiesOptions
            })
        }
    }
    const loadCountriesOptions = async () => {
        const res = await CountriesApiService.getOptions(dispatch)
        if (res.IsOk && res.Content) {
            setCountriesOptions(res.Content.map(c => ({ label: c.label, value: c.value } as SelectOptionModel)))            
        }
    }
    const loadCitiesOptions = async (countryId: number) => {
        if(citiesOptions.has(countryId)) {
            return
        }
        const res = await CitiesApiService.getCitiesInCountries(dispatch, [countryId])
        if (res.IsOk && res.Content) {
            const resCitiesOptions = res.Content.map(c => ({ label:c.name, value: c.id } as SelectOptionModel)).sort(defaultLabelSort)
            setCitiesOptions(prevCitiesOptions => {
                const newCitiesOptions = new Map<number, SelectOptionModel[]>(prevCitiesOptions)
                newCitiesOptions.set(countryId, resCitiesOptions)
                return newCitiesOptions
            })            
        }
    }
    const getCities = async () => {
        if (props.apiProviderId) {
            const citiesResponse = await ExternalIntegrationsApiService.getExternalCitiesMappings(dispatch, props.apiProviderId.toString())
            if (citiesResponse.IsOk && citiesResponse.Content) {
                setExternalCities(citiesResponse.Content)
            }
        }
    }
    const initDataTable = () => {
        const columns = [
            new DataTableColumnModel({
                displayName: 'API Integration City',
                fieldName: 'apiIntegrationCityName',
                filterType: FilterType.TEXT,
            }),
            new DataTableColumnModel({
                displayName: 'Our Country',
                fieldName: 'ourCountryId',
                isEditable:true,
                filterType: FilterType.SELECT,                   
                filterOptions: countriesOptions,
                customEditComponent: (obj: ExternalCityMappingModel, onChange: (value: any, fieldName: string) => void) => 
                {
                    console.log(obj)
                    return (
                        <div style={{border: '1px solid #ccc', borderRadius: '4px'}}>
                    <Select options={countriesOptions} 
                        value={obj.ourCountryId} 
                        onChange={(e: SelectOptionModel) => {
                            console.log(e)
                            onChange(e.value as number, 'ourCountryId')
                            loadCitiesOptions(e.value as number)
                        }} 
                        isClearable={true} />
                    </div>)
                },
                customRenderer: (obj: ExternalCityMappingModel) => obj.ourCountryId ? countriesHash.get(obj.ourCountryId as number) ?? 'NOT LOADED' : ''

            }),
            new DataTableColumnModel({
                displayName: 'Our City',
                fieldName: 'ourCityId',
                isEditable: true,
                editType: InputType.Select,
                customEditComponent: (obj: ExternalCityMappingModel, onChange: (value: any, fieldName: string) => void) => 
                {
                    console.log(obj)
                    return (
                        <div style={{border: '1px solid #ccc', borderRadius: '4px'}}>
                        <Select options={citiesOptions.get(obj.ourCountryId as number) ?? []} 
                            value={obj.ourCityId} 
                            onChange={(e: SelectOptionModel) => {
                                onChange(e.value as number, 'ourCityId')
                            }} 
                            isClearable={true} />
                        </div>)
                },
                customRenderer: (obj: ExternalCityMappingModel) => obj.ourCityId
                    ? citiesHash.get(obj.ourCountryId as number)?.get(obj.ourCityId as number) ?? 'NOT LOADED'
                    : ''
            })
        ]
        return new DataTableModel({
            columns,
            name: 'Cities Mappings',
        })
    }
    const onSetMapping = async (prevObj: ExternalCityMappingModel, obj: ExternalCityMappingModel) => {
        if (prevObj.ourCityId !== obj.ourCityId) {
            const payload = { apiProviderId: props.apiProviderId as number, cityId: obj.ourCityId, countryId: obj.ourCountryId as number, externalCityId: obj.apiIntegrationCityId } as ExternalCityMappingPostModel
            const response = await ExternalIntegrationsApiService.saveExternalCitiesMapping(dispatch, payload)
            if (response.IsOk && response.Content) {
                setExternalCities(prevCities => prevCities.map(city => city.apiIntegrationCityId === obj.apiIntegrationCityId ? response.Content ?? city : city))
            }
        }
    }
    const onRefreshData = async () => {
        await getCities()
    }
    return <DataTable
        data={externalCities}
        dataTable={initDataTable()}
        onRefreshData={onRefreshData}
        isFullScreen
        fixedHeight={600}
        onEdit={onSetMapping} />
}
export default AdminExternalCitiesMappings
